
import React from "react";
import { Link } from "react-router-dom";
import { 
  Clock, Bookmark, ThumbsUp, ThumbsDown, 
  MessageCircle, Check, Users, Shield, Settings,
  BarChart, AlertTriangle, Filter, Eye, Bell
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";

interface AdminGuideContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const AdminGuideContent: React.FC<AdminGuideContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  const renderAdminOverview = () => {
    const articleId = "admin-overview";
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Admin Overview</h1>
          <p className="text-gray-500 mt-2">
            Get started with the administrative features of the platform.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: August 15, 2023</span>
            <Badge variant="outline" className="ml-2">Admin Only</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="admin-dashboard">Admin Dashboard</h2>
          <p>
            The Admin Dashboard provides a central location for managing all aspects of the platform. 
            As an administrator, you have access to powerful tools for user management, content moderation, 
            system settings, and comprehensive analytics.
          </p>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-xl border border-purple-100 my-6">
            <div className="flex items-center mb-4">
              <div className="bg-purple-500 p-2 rounded-full mr-3">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-purple-800">Admin Privileges</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-gray-700">Manage all users and their permissions</p>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-gray-700">Monitor platform health and performance</p>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-gray-700">Review and moderate content</p>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-gray-700">Configure system-wide settings</p>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-gray-700">Access detailed analytics and reports</p>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-gray-700">Manage billing and subscription details</p>
              </div>
            </div>
          </div>

          <h2 id="accessing-admin-panel">Accessing the Admin Panel</h2>
          <p>
            The Admin Panel can be accessed by administrators by clicking on the Admin icon in the 
            sidebar navigation or by navigating directly to <code>/admin</code> in the URL. You must 
            have administrator privileges to access this area.
          </p>

          <h3 id="admin-dashboard-sections">Admin Dashboard Sections</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-3">
                  <Users className="h-5 w-5 text-indigo-500 mr-2" />
                  <h3 className="font-medium">User Management</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Manage user accounts, permissions, roles, and account statuses. Access user activity logs and authentication history.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2" />
                  <h3 className="font-medium">Content Moderation</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Review flagged content, moderate posts, manage reports, and enforce community guidelines.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-3">
                  <Settings className="h-5 w-5 text-blue-500 mr-2" />
                  <h3 className="font-medium">System Settings</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Configure platform settings, branding options, email templates, integration settings, and security policies.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-3">
                  <BarChart className="h-5 w-5 text-green-500 mr-2" />
                  <h3 className="font-medium">Analytics Dashboard</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Access platform-wide metrics, user acquisition data, engagement reports, and financial analytics.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-3">
                  <Bell className="h-5 w-5 text-red-500 mr-2" />
                  <h3 className="font-medium">Notifications</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Manage system-wide notifications, announcements, and alerts for all users or specific groups.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center mb-3">
                  <Shield className="h-5 w-5 text-purple-500 mr-2" />
                  <h3 className="font-medium">Security Center</h3>
                </div>
                <p className="text-sm text-gray-500">
                  Monitor security threats, review login attempts, manage API keys, and configure security settings.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-8">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              Admin Dashboard Training
            </h3>
            <p className="text-sm text-blue-700 mb-3">
              New to the admin role? Check out our comprehensive training resources for platform administrators.
            </p>
            <Button size="sm">
              Access Admin Training
            </Button>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted[articleId] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback(articleId, true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback(articleId, false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes(articleId) ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark(articleId)}
            >
              {bookmarkedArticles.includes(articleId) ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderUserManagement = () => {
    const articleId = "user-management";
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">User Management</h1>
          <p className="text-gray-500 mt-2">
            Learn how to manage users, roles, and permissions in the platform.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: August 10, 2023</span>
            <Badge variant="outline" className="ml-2">Admin Only</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="user-management-overview">User Management Overview</h2>
          <p>
            The User Management section allows administrators to manage all aspects of user accounts
            within the platform. You can create, edit, and deactivate user accounts, assign roles and
            permissions, and monitor user activity.
          </p>

          <Tabs defaultValue="roles" className="mt-6">
            <TabsList className="grid grid-cols-4 w-full max-w-2xl">
              <TabsTrigger value="roles">Roles & Permissions</TabsTrigger>
              <TabsTrigger value="accounts">Account Management</TabsTrigger>
              <TabsTrigger value="activity">Activity Monitoring</TabsTrigger>
              <TabsTrigger value="bulk">Bulk Operations</TabsTrigger>
            </TabsList>
            <TabsContent value="roles" className="pt-4">
              <h3 className="text-lg font-medium mb-3">Roles & Permissions</h3>
              <p className="mb-4">
                The platform uses a role-based access control system to manage permissions. Default roles include:
              </p>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <h4 className="font-medium mb-2">Administrator</h4>
                  <p className="text-sm text-gray-600 mb-2">Full access to all platform features and administration tools.</p>
                  <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200 border-purple-200">Highest Access Level</Badge>
                </div>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <h4 className="font-medium mb-2">Moderator</h4>
                  <p className="text-sm text-gray-600 mb-2">Access to content moderation tools and user management (limited).</p>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200">Medium Access Level</Badge>
                </div>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <h4 className="font-medium mb-2">Agency Owner</h4>
                  <p className="text-sm text-gray-600 mb-2">Can manage agency settings, team members, and billing.</p>
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-200 border-green-200">Business Access</Badge>
                </div>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <h4 className="font-medium mb-2">Client</h4>
                  <p className="text-sm text-gray-600 mb-2">Can post projects, hire freelancers, and manage own content.</p>
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-200">Client Access</Badge>
                </div>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <h4 className="font-medium mb-2">Freelancer</h4>
                  <p className="text-sm text-gray-600 mb-2">Can apply to projects, manage portfolio, and receive payments.</p>
                  <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-200 border-sky-200">Standard Access</Badge>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="accounts" className="pt-4">
              <h3 className="text-lg font-medium mb-3">Account Management</h3>
              <p className="mb-4">
                As an administrator, you can perform various account management tasks:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Create new user accounts manually</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Edit user profile information and contact details</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Reset passwords and manage account security settings</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Deactivate or suspend accounts when necessary</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Verify user identities and manage verification status</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Assign and revoke specific permissions</span>
                </li>
              </ul>
            </TabsContent>
            <TabsContent value="activity" className="pt-4">
              <h3 className="text-lg font-medium mb-3">Activity Monitoring</h3>
              <p className="mb-4">
                The platform provides comprehensive activity monitoring tools:
              </p>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Eye className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">Login Activity</span>
                    <p className="text-sm text-gray-600">Track login times, locations, and devices used</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Eye className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">Content Creation</span>
                    <p className="text-sm text-gray-600">Monitor projects, proposals, and other content created</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Eye className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">Transaction History</span>
                    <p className="text-sm text-gray-600">Review payments, withdrawals, and other financial activities</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Eye className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">Communication</span>
                    <p className="text-sm text-gray-600">Access message histories when needed for support or moderation</p>
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="bulk" className="pt-4">
              <h3 className="text-lg font-medium mb-3">Bulk Operations</h3>
              <p className="mb-4">
                For efficient user management, you can perform bulk operations:
              </p>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <h4 className="font-medium mb-2">Bulk Invite</h4>
                  <p className="text-sm text-gray-600">Upload a CSV file with email addresses to send multiple invitation emails at once.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <h4 className="font-medium mb-2">Bulk Role Assignment</h4>
                  <p className="text-sm text-gray-600">Select multiple users and assign the same role or permissions to all of them.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <h4 className="font-medium mb-2">Bulk Status Update</h4>
                  <p className="text-sm text-gray-600">Change the status (active, suspended, etc.) for multiple accounts at once.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <h4 className="font-medium mb-2">Bulk Export</h4>
                  <p className="text-sm text-gray-600">Export user data in CSV format for backup or analysis purposes.</p>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <h2 id="filter-search-users" className="mt-8">Filtering and Searching Users</h2>
          
          <div className="my-6">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="flex items-center mb-4">
                <Filter className="h-5 w-5 text-indigo-500 mr-2" />
                <h3 className="text-lg font-medium">User Search & Filter Options</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Filter By:</h4>
                  <ul className="space-y-1 text-sm">
                    <li>Registration date range</li>
                    <li>Account status (active, suspended, etc.)</li>
                    <li>Role type</li>
                    <li>Verification status</li>
                    <li>Subscription tier</li>
                    <li>Geographic location</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Search By:</h4>
                  <ul className="space-y-1 text-sm">
                    <li>Email address</li>
                    <li>Username</li>
                    <li>Full name</li>
                    <li>User ID</li>
                    <li>Phone number</li>
                    <li>Company/Organization</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4">
                <h4 className="font-medium mb-2">Advanced Search:</h4>
                <p className="text-sm">
                  Use the advanced search functionality to combine multiple criteria. For example, 
                  find all freelancers with verified accounts who joined in the last 30 days and have 
                  a specific skill tag.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-8">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Best Practices for User Management
            </h3>
            <p className="text-sm text-blue-700 mb-3">
              Check out our detailed guide on best practices for managing users safely and efficiently.
            </p>
            <Button size="sm">
              View Best Practices Guide
            </Button>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted[articleId] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback(articleId, true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback(articleId, false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes(articleId) ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark(articleId)}
            >
              {bookmarkedArticles.includes(articleId) ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (activePage === "admin-overview") {
    return renderAdminOverview();
  }

  if (activePage === "user-management") {
    return renderUserManagement();
  }

  return null;
};
